import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Phone, Mail, Clock, MapPin, Send } from 'lucide-react';
import { api } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { trackFormSubmission } from '../lib/analytics';

export default function Contact() {
  const [form, setForm] = useState({ 
    firstname: '', 
    email: '', 
    phone: '', 
    message: '', 
    data_processing_consent: false,
    marketingConsent: false 
  });
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  function update(field) {
    return (e) => setForm({ ...form, [field]: e.target.value });
  }

  function updateCheckbox(field) {
    return (e) => setForm({ ...form, [field]: e.target.checked });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.contact({
        ...form,
        data_processing_consent: String(form.data_processing_consent),
        marketing_consent: String(form.marketingConsent),
        consent_timestamp: new Date().toISOString()
      });
      // Track form submission
      trackFormSubmission('Contact Form', 'contact');
      
      toast({
        title: "Message Sent!",
        description: "Thanks! We will get back to you within 24 hours.",
        variant: "success"
      });
      setForm({ 
        firstname: '', 
        email: '', 
        phone: '', 
        message: '', 
        data_processing_consent: false,
        marketingConsent: false 
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || 'Submission failed. Please try again.',
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <section id="contact" className="relative py-24 bg-[#070E1B] text-white overflow-hidden">
      {/* ── Background Lighting ───────────────── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-1/3 -left-32 w-[500px] h-[500px] bg-[#C9A84C]/10 rounded-full blur-[150px] animate-pulse-glow" />
        <div className="absolute -bottom-20 right-10 w-[500px] h-[500px] bg-[#3D6ACC]/15 rounded-full blur-[150px]" />
        <div 
          className="absolute inset-0 opacity-[0.05]" 
          style={{
            backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px)`,
            backgroundSize: '32px 32px'
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs sm:text-sm font-semibold text-[#E8D4A0] tracking-wider uppercase">
            <Send className="w-4 h-4 text-[#C9A84C]" />
            Direct Communication Hub
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-playfair tracking-tight">
            Connect With Our <span className="text-gradient-gold">Licensed Wealth Specialists</span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto font-opensans leading-relaxed">
            Have questions about IRC Sec. 7702 tax-free growth, living benefits, or annuities? Send us a direct inquiry.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Contact Form Card */}
          <div className="lg:col-span-7 bg-[#162236]/90 border border-[#C9A84C]/30 rounded-2xl p-8 sm:p-10 shadow-2xl backdrop-blur-xl relative">
            <div className="space-y-2 mb-8 border-b border-slate-700/60 pb-6">
              <h3 className="text-2xl font-bold font-playfair text-white flex items-center gap-3">
                <Send className="h-6 w-6 text-[#C9A84C]" />
                Send a Direct Message
              </h3>
              <p className="text-xs sm:text-sm text-slate-300">
                Our licensed team typically responds within 2 business hours.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="firstname" className="text-xs font-semibold uppercase text-slate-300 tracking-wider">Full Name</label>
                  <Input id="firstname" placeholder="Your full name..." className="h-12 bg-[#0D1B2A] border-[#C9A84C]/30 text-white placeholder-slate-400 focus:border-[#C9A84C] rounded-xl" value={form.firstname} onChange={update('firstname')} required />
                </div>
                <div className="space-y-2">
                  <label htmlFor="email" className="text-xs font-semibold uppercase text-slate-300 tracking-wider">Email Address</label>
                  <Input id="email" type="email" placeholder="your@email.com" className="h-12 bg-[#0D1B2A] border-[#C9A84C]/30 text-white placeholder-slate-400 focus:border-[#C9A84C] rounded-xl" value={form.email} onChange={update('email')} required />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="phone" className="text-xs font-semibold uppercase text-slate-300 tracking-wider">Phone Number</label>
                <Input id="phone" type="tel" placeholder="(208) 555-0123" className="h-12 bg-[#0D1B2A] border-[#C9A84C]/30 text-white placeholder-slate-400 focus:border-[#C9A84C] rounded-xl" value={form.phone} onChange={update('phone')} />
              </div>              

              <div className="space-y-2">
                <label htmlFor="message" className="text-xs font-semibold uppercase text-slate-300 tracking-wider">Your Inquiry</label>
                <Textarea 
                  id="message" 
                  placeholder="Tell us about your financial goals, questions, or protection requirements..."
                  className="min-h-[120px] bg-[#0D1B2A] border-[#C9A84C]/30 text-white placeholder-slate-400 focus:border-[#C9A84C] rounded-xl resize-none text-sm"
                  value={form.message}
                  onChange={update('message')}
                  required
                />
              </div>

              {/* Consent Checkboxes */}
              <div className="space-y-3 pt-1">
                <div className="flex items-start gap-3">
                  <input 
                    type="checkbox" 
                    id="privacy-consent-contact" 
                    checked={form.data_processing_consent}
                    onChange={updateCheckbox('data_processing_consent')}
                    required
                    className="mt-1 h-4 w-4 rounded accent-[#C9A84C] bg-[#0D1B2A] border-[#C9A84C]/40"
                  />
                  <label htmlFor="privacy-consent-contact" className="text-xs text-slate-300 leading-relaxed">
                    I consent to GrowAndStayRich processing my personal data to respond to my inquiry. {" "}
                    <a href="#privacy" className="text-[#C9A84C] hover:underline">Privacy Policy</a>
                  </label>
                </div>
                
                <div className="flex items-start gap-3">
                  <input 
                    type="checkbox" 
                    id="marketing-consent-contact" 
                    checked={form.marketingConsent}
                    onChange={updateCheckbox('marketingConsent')}
                    className="mt-1 h-4 w-4 rounded accent-[#C9A84C] bg-[#0D1B2A] border-[#C9A84C]/40"
                  />
                  <label htmlFor="marketing-consent-contact" className="text-xs text-slate-300 leading-relaxed">
                    I would like to receive financial insights & masterclass invitations (optional).
                  </label>
                </div>
              </div>
              
              <Button 
                type="submit" 
                disabled={submitting} 
                className="w-full h-14 text-base font-bold bg-gradient-gold-cta text-[#070E1B] rounded-xl shadow-xl hover:opacity-95 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Send className="h-5 w-5" />
                {submitting ? 'Sending Message…' : 'Transmit Message'}
              </Button>
            </form>
            
            {/* Privacy Guarantee */}
            <div className="mt-6 p-3.5 bg-[#0D1B2A] border border-[#C9A84C]/20 rounded-xl text-xs text-slate-300 text-center">
              🔒 Your contact information is kept strictly confidential and never shared with third parties.
            </div>
          </div>

          {/* Contact Details Column */}
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-[#162236]/90 border border-[#C9A84C]/30 rounded-2xl p-8 shadow-2xl backdrop-blur-xl space-y-6">
              <div className="space-y-1 border-b border-slate-700/60 pb-4">
                <h3 className="text-xl font-bold font-playfair text-white">Direct Advisory Contact</h3>
                <p className="text-xs text-slate-300">Reach out directly via phone, email, or online schedule</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-[#0D1B2A] border border-[#C9A84C]/20 rounded-xl hover:border-[#C9A84C]/50 transition-all duration-300">
                  <div className="p-3 bg-[#162236] border border-[#C9A84C]/30 rounded-xl text-[#C9A84C]">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <a href="tel:9032313051" className="font-bold text-base text-white hover:text-[#E8D4A0] transition-colors">(903) 231-3051</a>
                    <div className="text-xs text-slate-400 font-medium">Direct Advisory & TCPA Support</div>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-[#0D1B2A] border border-[#C9A84C]/20 rounded-xl hover:border-[#C9A84C]/50 transition-all duration-300">
                  <div className="p-3 bg-[#162236] border border-[#C9A84C]/30 rounded-xl text-[#C9A84C]">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="font-bold text-base text-white">info@growandstayrich.com</div>
                    <div className="text-xs text-slate-400">Send us an email</div>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-[#0D1B2A] border border-[#C9A84C]/20 rounded-xl hover:border-[#C9A84C]/50 transition-all duration-300">
                  <div className="p-3 bg-[#162236] border border-[#C9A84C]/30 rounded-xl text-[#C9A84C]">
                    <Clock className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="font-bold text-base text-white">Monday - Friday</div>
                    <div className="text-xs text-slate-400">9:00 AM - 6:00 PM MST</div>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-[#0D1B2A] border border-[#C9A84C]/20 rounded-xl hover:border-[#C9A84C]/50 transition-all duration-300">
                  <div className="p-3 bg-[#162236] border border-[#C9A84C]/30 rounded-xl text-[#C9A84C]">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="font-bold text-base text-white">Nationwide Coverage</div>
                    <div className="text-xs text-slate-400">Licensed & Insured Across The US</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fast Response Guarantee */}
            <div className="bg-[#0D1B2A]/90 border border-[#C9A84C]/25 rounded-2xl p-8 shadow-2xl backdrop-blur-xl text-center space-y-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                <span>Fast Response Commitment</span>
              </div>
              <h4 className="text-lg font-bold font-playfair text-white">Dedicated Client Support</h4>
              <p className="text-xs text-slate-300 leading-relaxed">
                All client inquiries receive priority review by our licensed wealth specialists. We are here to serve your financial security.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}